import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Receipt } from '@/types/order';
import { 
  FileText, 
  Filter, 
  Download, 
  Printer, 
  Eye, 
  Search,
  X,
  RefreshCw
} from 'lucide-react';
import ReceiptPrintView from '../OrderDetailsV2/ReceiptPrintView';
import { formatDate, formatCurrency } from '../../utils';

interface ReceiptsTabProps {
  userRole?: 'customer' | 'tailor' | 'admin';
}

interface ReceiptWithOrder extends Receipt {
  order?: {
    id: string;
    orderNumber?: string;
    customer?: {
      name?: string;
    };
  };
}

const ReceiptsTab: React.FC<ReceiptsTabProps> = ({ userRole }) => {
  const { user, token } = useAuth();
  const [receipts, setReceipts] = useState<ReceiptWithOrder[]>([]);
  const [filteredReceipts, setFilteredReceipts] = useState<ReceiptWithOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedOrder, setSelectedOrder] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // View/Print states
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptWithOrder | null>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  
  // Available orders for filter dropdown
  const [availableOrders, setAvailableOrders] = useState<Array<{ id: string; orderNumber?: string }>>([]);

  // Payment method options
  const paymentMethods = [
    { value: '', label: 'All Methods' },
    { value: 'cash', label: 'Cash' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'upi', label: 'UPI' },
    { value: 'razorpay', label: 'Razorpay' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'fully_paid', label: 'Fully Paid' },
    { value: 'partially_paid', label: 'Partially Paid' },
  ];

  // Fetch receipts from API
  const fetchReceipts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!token) {
        setError('Authentication required');
        setIsLoading(false);
        return;
      }

      const apiUrl = API_BASE;
      const params = new URLSearchParams();
      
      if (selectedOrder) {
        params.append('order_id', selectedOrder);
      }
      if (dateFrom) {
        params.append('date_from', new Date(dateFrom).toISOString());
      }
      if (dateTo) {
        const dateToObj = new Date(dateTo);
        dateToObj.setHours(23, 59, 59, 999); // End of day
        params.append('date_to', dateToObj.toISOString());
      }
      if (paymentMethod) {
        params.append('payment_method', paymentMethod);
      }

      const url = `${apiUrl}/api/receipts${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please log in again.');
          return;
        }
        throw new Error(`Failed to fetch receipts: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && data.receipts) {
        // Map API response to frontend Receipt type (snake_case to camelCase)
        const mappedReceipts = data.receipts.map((r: any) => ({
          id: r.id,
          receiptNumber: r.receipt_number,
          orderId: r.order_id,
          paymentId: r.payment_id,
          invoiceId: r.invoice_id,
          amount: r.amount,
          remainingBalance: r.remaining_balance,
          paymentMethod: r.payment_method,
          transactionId: r.transaction_id,
          paymentDate: r.payment_date,
          receiptDate: r.receipt_date,
          gstAmount: r.gst_amount,
          gstPercentage: r.gst_percentage,
          cgstAmount: r.cgst_amount,
          sgstAmount: r.sgst_amount,
          igstAmount: r.igst_amount,
          notes: r.notes,
          createdBy: r.created_by,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        }));
        
        // Fetch order details for each receipt
        const receiptsWithOrders = await Promise.all(
          mappedReceipts.map(async (receipt: Receipt) => {
            try {
              const orderResponse = await fetch(`${apiUrl}/api/orders/${receipt.orderId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              if (orderResponse.ok) {
                const orderData = await orderResponse.json();
                return {
                  ...receipt,
                  order: orderData.data || { id: receipt.orderId }
                };
              }
            } catch (_err) {
              console.error('Error fetching order details:', err);
            }
            return { ...receipt, order: { id: receipt.orderId } };
          })
        );
        
        setReceipts(receiptsWithOrders);
        setFilteredReceipts(receiptsWithOrders);
      } else {
        setReceipts([]);
        setFilteredReceipts([]);
      }
    } catch (err: any) {
      console.error('Error fetching receipts:', err);
      setError(err.message || 'Failed to load receipts');
      setReceipts([]);
      setFilteredReceipts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available orders for filter dropdown
  const fetchAvailableOrders = async () => {
    try {
      if (!token) return;

      const apiUrl = API_BASE;
      const response = await fetch(`${apiUrl}/api/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.orders) {
          setAvailableOrders(data.orders.map((o: any) => ({
            id: o.id,
            orderNumber: o.order_number
          })));
        }
      }
    } catch (_err) {
      console.error('Error fetching orders:', err);
    }
  };

  // Effect to fetch receipts when filters change
  useEffect(() => {
    fetchReceipts();
    fetchAvailableOrders();
  }, [selectedOrder, dateFrom, dateTo, paymentMethod, token]);

  // Effect to filter receipts by search term
  useEffect(() => {
    if (!searchTerm && !paymentStatus) {
      setFilteredReceipts(receipts);
      return;
    }

    let filtered = receipts;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(receipt => {
        return (
          receipt.receiptNumber.toLowerCase().includes(searchLower) ||
          receipt.order?.orderNumber?.toLowerCase().includes(searchLower) ||
          receipt.paymentMethod.toLowerCase().includes(searchLower) ||
          receipt.transactionId?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by payment status
    if (paymentStatus) {
      filtered = filtered.filter(receipt => {
        if (paymentStatus === 'fully_paid') {
          return receipt.remainingBalance <= 0;
        } else if (paymentStatus === 'partially_paid') {
          return receipt.remainingBalance > 0;
        }
        return true;
      });
    }

    setFilteredReceipts(filtered);
  }, [searchTerm, paymentStatus, receipts]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedOrder('');
    setDateFrom('');
    setDateTo('');
    setPaymentMethod('');
    setPaymentStatus('');
    setSearchTerm('');
  };

  // Format payment method for display
  const formatPaymentMethod = (method: string) => {
    const methodMap: Record<string, string> = {
      cash: 'Cash',
      credit_card: 'Credit Card',
      debit_card: 'Debit Card',
      upi: 'UPI',
      razorpay: 'Razorpay',
      stripe: 'Stripe',
      cheque: 'Cheque',
      bank_transfer: 'Bank Transfer',
      simulated: 'Simulated',
    };
    return methodMap[method.toLowerCase()] || method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Handle view receipt
  const handleViewReceipt = async (receipt: ReceiptWithOrder) => {
    try {
      if (!token) return;

      const apiUrl = API_BASE;
      const response = await fetch(`${apiUrl}/api/orders/${receipt.orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrderDetails(data.data);
        setSelectedReceipt(receipt);
        setShowPrintView(true);
      }
    } catch (_err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details');
    }
  };

  // Handle print receipt
  const handlePrintReceipt = async (receipt: ReceiptWithOrder) => {
    await handleViewReceipt(receipt);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setShowPrintView(false);
        setSelectedReceipt(null);
      }, 500);
    }, 100);
  };

  // Handle download receipt (as PDF)
  const handleDownloadReceipt = async (receipt: ReceiptWithOrder) => {
    await handleViewReceipt(receipt);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setShowPrintView(false);
        setSelectedReceipt(null);
      }, 500);
    }, 100);
  };

  // Calculate total receipts amount
  const totalAmount = filteredReceipts.reduce((sum, r) => sum + r.amount, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Receipts</h2>
              {filteredReceipts.length > 0 && (
                <span className="text-sm text-gray-500">
                  ({filteredReceipts.length} {filteredReceipts.length === 1 ? 'receipt' : 'receipts'})
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showFilters || selectedOrder || dateFrom || dateTo || paymentMethod || paymentStatus
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4 inline mr-2" />
                Filters
              </button>
              <button
                onClick={fetchReceipts}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 inline mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="lg:col-span-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by receipt number, order number, or transaction ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Order Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>
                <select
                  value={selectedOrder}
                  onChange={(e) => setSelectedOrder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Orders</option>
                  {availableOrders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.orderNumber || order.id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {paymentStatusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              {(selectedOrder || dateFrom || dateTo || paymentMethod || paymentStatus) && (
                <div className="lg:col-span-4">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {filteredReceipts.length > 0 && (
          <div className="px-6 py-3 bg-blue-50 border-b border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Total Receipts: <span className="font-semibold text-gray-900">{filteredReceipts.length}</span>
              </span>
              <span className="text-gray-600">
                Total Amount: <span className="font-semibold text-blue-600">{formatCurrency(totalAmount)}</span>
              </span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="px-6 py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading receipts...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="px-6 py-12 text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-red-800 font-medium mb-2">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Receipts List */}
        {!isLoading && !error && filteredReceipts.length === 0 && (
          <div className="px-6 py-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium mb-2">No receipts found</p>
            <p className="text-gray-500 text-sm">
              {selectedOrder || dateFrom || dateTo || paymentMethod
                ? 'Try adjusting your filters'
                : 'Receipts will appear here once payments are processed'}
            </p>
          </div>
        )}

        {!isLoading && !error && filteredReceipts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remaining Balance
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReceipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{receipt.receiptNumber}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {receipt.order?.orderNumber || receipt.orderId}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(receipt.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {formatPaymentMethod(receipt.paymentMethod)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {formatDate(receipt.paymentDate)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        receipt.remainingBalance > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {formatCurrency(receipt.remainingBalance)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewReceipt(receipt)}
                          className="text-blue-600 hover:text-blue-900 p-1.5 rounded hover:bg-blue-50 transition-colors"
                          title="View Receipt"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadReceipt(receipt)}
                          className="text-gray-600 hover:text-gray-900 p-1.5 rounded hover:bg-gray-50 transition-colors"
                          title="Download Receipt"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrintReceipt(receipt)}
                          className="text-gray-600 hover:text-gray-900 p-1.5 rounded hover:bg-gray-50 transition-colors"
                          title="Print Receipt"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Print View Modal */}
      {showPrintView && selectedReceipt && orderDetails && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">Receipt Preview</h3>
            <button
              onClick={() => {
                setShowPrintView(false);
                setSelectedReceipt(null);
                setOrderDetails(null);
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
          <ReceiptPrintView receipt={selectedReceipt} order={orderDetails} />
        </div>
      )}
    </motion.div>
  );
};

export default ReceiptsTab;

